import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import PromoBanner from '@/components/PromoBanner';
import RestaurantCard from '@/components/RestaurantCard';
import { Button } from '@/components/ui/button';
import { ChevronRight } from 'lucide-react';
import groceriesImg from '@/assets/mcat2-groceries.png';
import beautyImg from '@/assets/mcat2-beauty.png';
import technologyImg from '@/assets/mcat2-technology.png';
import clothingImg from '@/assets/mcat2-clothing.png';
import homeEssentialsImg from '@/assets/mcat2-home-essentials.png';
import servicesImg from '@/assets/mcat2-services.png';
import sheinLogo from '@/assets/shein-logo.png';

const categories = [
  { id: 'groceries', name: 'Groceries', image: groceriesImg },
  { id: 'beauty', name: 'Beauty', image: beautyImg },
  { id: 'technology', name: 'Technology', image: technologyImg },
  { id: 'clothing', name: 'Clothing', image: clothingImg },
  { id: 'home_essentials', name: 'Home Essentials', image: homeEssentialsImg },
  { id: 'services', name: 'Services', image: servicesImg },
];

interface Vendor {
  id: string;
  name: string;
  description: string | null;
  image_url: string | null;
  rating: number;
  address: string;
  categories: string[] | null;
  created_at: string | null;
  is_open: boolean;
  opening_hours: any;
  avg_prep_time_min: number | null;
  avg_prep_time_max: number | null;
  average_rating?: number;
}

interface MarketplaceHomeProps {
  showInternational?: boolean;
}

const MarketplaceHome = ({ showInternational = false }: MarketplaceHomeProps) => {
  const navigate = useNavigate();
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  useEffect(() => {
    const fetchVendors = async () => {
      try {
        const { data, error } = await supabase
          .from('restaurants')
          .select(
            'id, name, description, image_url, rating, address, categories, created_at, is_open, opening_hours, avg_prep_time_min, avg_prep_time_max'
          )
          .eq('business_type', 'marketplace');

        if (error) throw error;

        const list = (data || []) as Vendor[];

        // Real average ratings from the ratings table
        const ids = list.map(v => v.id);
        let averages: Record<string, number> = {};
        if (ids.length > 0) {
          const { data: ratingsData } = await supabase
            .from('ratings')
            .select('restaurant_id, restaurant_rating')
            .in('restaurant_id', ids)
            .not('restaurant_rating', 'is', null);

          const totals: Record<string, { sum: number; count: number }> = {};
          (ratingsData || []).forEach(r => {
            const key = r.restaurant_id as string;
            if (!totals[key]) totals[key] = { sum: 0, count: 0 };
            totals[key].sum += Number(r.restaurant_rating);
            totals[key].count += 1;
          });
          averages = Object.fromEntries(
            Object.entries(totals).map(([k, v]) => [k, v.sum / v.count])
          );
        }

        const withRatings = list.map(v => ({
          ...v,
          average_rating: averages[v.id] ?? (v.rating || 0),
        }));

        const sorted = withRatings.sort((a, b) => {
          const aHasLogo = a.image_url && a.image_url !== '/placeholder.svg' ? 1 : 0;
          const bHasLogo = b.image_url && b.image_url !== '/placeholder.svg' ? 1 : 0;
          return bHasLogo - aHasLogo;
        });
        setVendors(sorted);
      } catch (error) {
        console.error('Error fetching marketplace vendors:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchVendors();
  }, []);

  const filteredVendors = useMemo(() => {
    if (!selectedCategory) return vendors;
    return vendors.filter(v => (v.categories || []).includes(selectedCategory));
  }, [vendors, selectedCategory]);

  const categorySections = useMemo(
    () =>
      categories
        .map(c => ({
          ...c,
          vendors: vendors.filter(v => (v.categories || []).includes(c.id)),
        }))
        .filter(c => c.vendors.length > 0),
    [vendors]
  );

  const toCardVendor = (vendor: Vendor) => ({
    id: vendor.id,
    name: vendor.name,
    description: vendor.description || '',
    image_url: vendor.image_url || '',
    rating: vendor.rating ?? 0,
    average_rating: vendor.average_rating,
    address: vendor.address || '',
    created_at: vendor.created_at || '',
    is_open: vendor.is_open,
    opening_hours: vendor.opening_hours,
    avg_prep_time_min: vendor.avg_prep_time_min ?? undefined,
    avg_prep_time_max: vendor.avg_prep_time_max ?? undefined,
  });


  return (
    <>
      {/* Categories */}
      <div className="-mx-4 px-4 mb-6 overflow-x-auto scrollbar-hide">
        <div className="flex gap-6 w-max items-start">
          {categories.map(category => (
            <button
              key={category.id}
              className="flex flex-col items-center gap-2 shrink-0 w-[72px]"
              onClick={() =>
                setSelectedCategory(selectedCategory === category.id ? null : category.id)
              }
            >
              <img
                src={category.image}
                alt={category.name}
                loading="lazy"
                width={512}
                height={512}
                className={`w-14 h-14 object-contain transition-transform ${
                  selectedCategory === category.id ? 'scale-110' : ''
                }`}
              />
              <span
                className={`text-[13px] leading-tight text-center w-full ${
                  selectedCategory === category.id
                    ? 'font-bold text-primary'
                    : 'font-semibold text-foreground'
                }`}
              >
                {category.name}
              </span>
            </button>
          ))}

          {showInternational && (
            <button
              className="flex flex-col items-center gap-2 shrink-0 w-[72px]"
              onClick={() => window.open('https://shein.com', '_blank', 'noopener,noreferrer')}
            >
              <img src={sheinLogo} alt="SHEIN" loading="lazy" className="w-14 h-14 object-contain" />
              <span className="text-[13px] leading-tight font-semibold text-foreground text-center w-full">
                SHEIN
              </span>
            </button>
          )}
        </div>
      </div>


      {selectedCategory && (
        <div className="mb-4">
          <Button variant="outline" size="sm" onClick={() => setSelectedCategory(null)}>
            Clear filter
          </Button>
        </div>
      )}

      {/* Banner */}
      {!selectedCategory && <PromoBanner type="marketplace" />}

      {loading ? (
        <div className="space-y-3 mt-4">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="h-24 rounded-lg bg-muted animate-pulse" />
          ))}
        </div>
      ) : selectedCategory ? (
        <div className="mb-2">
          <h2 className="text-lg font-semibold mb-3">
            {categories.find(c => c.id === selectedCategory)?.name} vendors
          </h2>
          {filteredVendors.length === 0 ? (
            <div className="text-center py-10 text-muted-foreground">
              <p className="font-medium">No vendors here yet</p>
              <p className="text-sm">Check back soon</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4">
              {filteredVendors.map(vendor => (
                <RestaurantCard
                  key={vendor.id}
                  restaurant={toCardVendor(vendor)}
                  variant="vertical"
                  onClick={() => navigate(`/marketplace/vendor/${vendor.id}`)}
                />
              ))}
            </div>
          )}
        </div>
      ) : (
        <>
          {/* Per-category vendor rows */}
          {categorySections.map(section => (
            <div key={section.id} className="mb-6">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-lg font-semibold">{section.name}</h2>
                <button
                  className="flex items-center text-sm text-muted-foreground"
                  onClick={() => setSelectedCategory(section.id)}
                >
                  All <ChevronRight className="w-4 h-4" />
                </button>
              </div>
              <div className="overflow-x-auto pb-2 scrollbar-hide">
                <div className="flex gap-4 w-max">
                  {section.vendors.map(vendor => (
                    <RestaurantCard
                      key={vendor.id}
                      restaurant={toCardVendor(vendor)}
                      variant="horizontal"
                      onClick={() => navigate(`/marketplace/vendor/${vendor.id}`)}
                    />
                  ))}
                </div>
              </div>
            </div>
          ))}

          {/* All vendors */}
          <div className="mb-2">
            <h2 className="text-lg font-semibold mb-3">All vendors</h2>
            {vendors.length === 0 ? (
              <div className="text-center py-10 text-muted-foreground">
                <p className="font-medium">No vendors here yet</p>
                <p className="text-sm">Check back soon</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-4">
                {vendors.map(vendor => (
                  <RestaurantCard
                    key={vendor.id}
                    restaurant={toCardVendor(vendor)}
                    variant="vertical"
                    onClick={() => navigate(`/marketplace/vendor/${vendor.id}`)}
                  />
                ))}
              </div>
            )}
          </div>
        </>
      )}

    </>
  );
};

export default MarketplaceHome;
